import {
  addScriptChunk,
  getState,
  removeScriptChunk,
  subscribe,
  updateScriptChunk,
} from '../store/project';
import type { ScriptChunk } from '../types';

export class ScriptModal {
  private bg!: HTMLElement;
  private listEl!: HTMLElement;
  private editorEl!: HTMLElement;
  private currentId: string | null = null;

  constructor() {
    this.build();
    subscribe(() => {
      if (this.bg.classList.contains('show')) {
        this.renderList();
        if (this.currentId) this.renderEditor();
      }
    });
  }

  private build(): void {
    this.bg = document.createElement('div');
    this.bg.className = 'modal-bg';
    const modal = document.createElement('div');
    modal.className = 'modal modal-lg';

    const h = document.createElement('h3');
    h.textContent = 'ⓢ 스크립트';
    const desc = document.createElement('p');
    desc.textContent =
      '여러 청크로 나눠 적어도 돼요. 활성 청크가 합쳐져서 적용 — CSS는 <head>에, JS는 매 프레임 실행. JS 인자: t(ms), project, callTool.';

    const split = document.createElement('div');
    split.className = 'script-split';

    this.listEl = document.createElement('div');
    this.listEl.className = 'script-list';

    this.editorEl = document.createElement('div');
    this.editorEl.className = 'script-editor';

    split.append(this.listEl, this.editorEl);

    const actions = document.createElement('div');
    actions.className = 'row-actions';
    const close = document.createElement('button');
    close.className = 'btn primary';
    close.textContent = '닫기';
    close.addEventListener('click', () => this.close());
    actions.appendChild(close);

    modal.append(h, desc, split, actions);
    this.bg.appendChild(modal);
    this.bg.addEventListener('click', (e) => {
      if (e.target === this.bg) this.close();
    });
    document.body.appendChild(this.bg);
  }

  open(): void {
    this.bg.classList.add('show');
    const chunks = getState().project.scripts ?? [];
    this.currentId = chunks[0]?.id ?? null;
    this.renderList();
    this.renderEditor();
  }
  close(): void {
    this.bg.classList.remove('show');
  }

  private renderList(): void {
    this.listEl.replaceChildren();
    const chunks = getState().project.scripts ?? [];

    const addRow = document.createElement('div');
    addRow.className = 'script-add-row';
    const addCss = document.createElement('button');
    addCss.className = 'btn ghost';
    addCss.textContent = '+ CSS';
    addCss.addEventListener('click', () => {
      const c = addScriptChunk('css');
      this.currentId = c.id;
      this.renderEditor();
    });
    const addJs = document.createElement('button');
    addJs.className = 'btn ghost';
    addJs.textContent = '+ JS';
    addJs.addEventListener('click', () => {
      const c = addScriptChunk('js');
      this.currentId = c.id;
      this.renderEditor();
    });
    addRow.append(addCss, addJs);
    this.listEl.appendChild(addRow);

    if (chunks.length === 0) {
      const empty = document.createElement('div');
      empty.className = 'script-empty';
      empty.textContent = '+ CSS 또는 + JS로 청크 추가';
      this.listEl.appendChild(empty);
      return;
    }

    for (const c of chunks) {
      const row = document.createElement('div');
      row.className = 'script-row';
      if (c.id === this.currentId) row.classList.add('active');
      const tag = document.createElement('span');
      tag.className = `script-tag ${c.lang}`;
      tag.textContent = c.lang.toUpperCase();
      const name = document.createElement('div');
      name.className = 'script-name';
      name.textContent = c.name;
      const toggle = document.createElement('input');
      toggle.type = 'checkbox';
      toggle.checked = c.enabled;
      toggle.title = '활성/비활성';
      toggle.addEventListener('click', (e) => {
        e.stopPropagation();
        updateScriptChunk(c.id, { enabled: toggle.checked });
      });
      const del = document.createElement('button');
      del.className = 'script-del';
      del.textContent = '✕';
      del.title = '삭제';
      del.addEventListener('click', (e) => {
        e.stopPropagation();
        if (!confirm(`"${c.name}" 삭제?`)) return;
        removeScriptChunk(c.id);
        if (this.currentId === c.id) {
          const remaining = (getState().project.scripts ?? []).filter((x) => x.id !== c.id);
          this.currentId = remaining[0]?.id ?? null;
          this.renderEditor();
        }
      });
      row.append(tag, name, toggle, del);
      row.addEventListener('click', () => {
        this.currentId = c.id;
        this.renderList();
        this.renderEditor();
      });
      this.listEl.appendChild(row);
    }
  }

  private renderEditor(): void {
    this.editorEl.replaceChildren();
    const chunk = (getState().project.scripts ?? []).find((c) => c.id === this.currentId);
    if (!chunk) {
      const empty = document.createElement('div');
      empty.className = 'script-empty';
      empty.style.padding = '40px 24px';
      empty.style.textAlign = 'center';
      empty.textContent = '왼쪽에서 청크를 선택하거나 새로 추가';
      this.editorEl.appendChild(empty);
      return;
    }

    const head = document.createElement('div');
    head.className = 'script-editor-head';
    const nameInput = document.createElement('input');
    nameInput.type = 'text';
    nameInput.value = chunk.name;
    nameInput.placeholder = '청크 이름';
    nameInput.addEventListener('input', () =>
      updateScriptChunk(chunk.id, { name: nameInput.value }),
    );
    head.appendChild(nameInput);

    const ta = document.createElement('textarea');
    ta.value = chunk.source;
    ta.spellcheck = false;
    ta.placeholder =
      chunk.lang === 'css'
        ? '/* CSS 청크 */\n.layer .your-class { ... }'
        : '// JS 청크 — 매 프레임 실행됨\n// 인자: t (ms), project, callTool';
    Object.assign(ta.style, {
      width: '100%',
      minHeight: '320px',
      fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace',
      fontSize: '12px',
      background: 'var(--bg-elev)',
      color: 'var(--ink)',
      border: '1px solid var(--line)',
      borderRadius: '8px',
      padding: '10px 12px',
      resize: 'vertical',
    } satisfies Partial<CSSStyleDeclaration>);
    ta.addEventListener('input', () =>
      updateScriptChunk(chunk.id, { source: ta.value }),
    );

    this.editorEl.append(head, ta);
  }
}

void ({} as ScriptChunk); // keep type alive for tooling
